import type { FleetV2Response } from '@emma/api_client/types';
import type { DeploymentStrategy } from '@emma/core/consts';
import {
  Button,
  ButtonGroup,
  Flex,
  Link,
  Menu,
  Modal,
  Spinner,
  Text,
  useToast,
} from '@lego/klik-ui';
import { ChevronDown } from '@lego/klik-ui/icons';
import { useState } from 'react';
import {
  ImmediateStrategyWarning,
  isImmediateStrategy,
} from '@/components/common/ImmediateStrategyWarning';
import { useDevices } from '@/hooks/device';
import { useUpdateFleet } from '@/hooks/fleet';
import { ToastOptions } from '@/utility/ToastOptions';

type Props = {
  fleet: FleetV2Response;
};

const STRATEGY_LABELS: Record<DeploymentStrategy, string> = {
  SCHEDULE: 'Schedule',
  IMMEDIATE_APPLICATION_ONLY: 'Immediate (Application Only)',
  IMMEDIATE: 'Immediate',
};

export const EditableDeploymentStrategyMenu = ({ fleet }: Props) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedStrategy, setSelectedStrategy] = useState<DeploymentStrategy | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const {
    data: devicesData,
    isLoading: devicesLoading,
    mutate: mutateDevices,
  } = useDevices({
    fleetIdIn: [fleet.id],
    workspaceIdIn: [fleet.workspaceId],
  });

  const updateFleet = useUpdateFleet(fleet.workspaceId, fleet.id);

  const handleStrategyChange = async (strategy: DeploymentStrategy): Promise<boolean> => {
    setIsLoading(true);
    try {
      await updateFleet({ deploymentStrategy: strategy });
      await mutateDevices();
      toast(
        ToastOptions.success(
          'Deployment strategy updated',
          `Updated ${devicesData?.devices.length} device(s) to ${STRATEGY_LABELS[strategy].toUpperCase()}`,
          5000
        )
      );
      return true;
    } catch {
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleMenuItemClick = (strategy: DeploymentStrategy) => {
    setSelectedStrategy(strategy);
    setIsOpen(true);
  };

  return (
    <>
      {devicesLoading ? (
        <Spinner size="sm" />
      ) : devicesData?.devices?.length && devicesData?.devices?.length > 0 ? (
        <Menu>
          <Menu.Button as={Link} tabIndex={0}>
            Change Strategy for all Devices <ChevronDown />
          </Menu.Button>

          <Menu.List>
            {Object.entries(STRATEGY_LABELS).map(([key, value]) => (
              <Menu.Item
                isDisabled={isLoading}
                key={key}
                onClick={() => handleMenuItemClick(key as DeploymentStrategy)}
              >
                {value}
              </Menu.Item>
            ))}
          </Menu.List>
        </Menu>
      ) : (
        <Text>No devices in this fleet</Text>
      )}

      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="xl">
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>Change Deployment Strategy for all Devices in this Fleet</Modal.Header>
          <Modal.CloseButton />
          <Modal.Body>
            Do you wish to change the Deployment Strategy for{' '}
            <Text as="span" fontWeight="bold">
              ({devicesData?.devices?.length})
            </Text>{' '}
            device(s) in this Fleet to{' '}
            <Text as="span" fontWeight="bold">
              {selectedStrategy ? STRATEGY_LABELS[selectedStrategy].toUpperCase() : ''}
            </Text>
            ? Only devices owned by your workspace will be updated.
            {isImmediateStrategy(selectedStrategy) && (
              <Flex align="center" color="warning.500" gap={2} mt={3}>
                <ImmediateStrategyWarning />
                <Text>
                  Immediate is risky. Rollout windows and schedules are much more predictable.
                </Text>
              </Flex>
            )}
          </Modal.Body>

          <Modal.Footer>
            <ButtonGroup>
              <Button
                isLoading={isLoading}
                onClick={async () => {
                  if (selectedStrategy) {
                    const success = await handleStrategyChange(selectedStrategy);
                    if (success) setIsOpen(false);
                  }
                }}
              >
                Confirm
              </Button>
              <Button onClick={() => setIsOpen(false)} variant="outline">
                Cancel
              </Button>
            </ButtonGroup>
          </Modal.Footer>
        </Modal.Content>
      </Modal>
    </>
  );
};
